import { Box, Button, Divider, Typography } from '@material-ui/core'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-calculate'
import get from 'lodash/get'
import merge from 'lodash/merge'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import set from 'lodash/set'
import split from 'lodash/split'
import union from 'lodash/union'
import { DateTime } from 'luxon'
import {
  ArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  CheckboxGroupInput,
  FormDataConsumer,
  Labeled,
  NumberInput,
  ReferenceInput,
  required,
  sanitizeEmptyValues,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  Toolbar,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin'
import { Form } from 'react-final-form'
import { PROVIDER_CHOICES } from 'shared/helpers/constants'

import { generateSchedule, prefillLocations } from '../../api/schedules'
import { SUPPORTED_EQUIPMENT_CHOICES, TRACTOR_CHOICES } from '../../helpers/constants'
import { US_STATES, ZIP_ZONE_STATES } from '../../helpers/states'
import { DateTimeInput24hr, ShowIfContains } from '../Fields'

const validateRequired = [required()]
const driverPreferencesFields = [
  'terminal_city',
  'terminal_state',
  'equipment',
  'tractor_type',
  'miles_per_hour',
  'truck_weight_lb',
]
const locationPrefillFields: Record<any, any> = {
  start_location_prefill: {
    city_field: 'start_city',
    state_field: 'start_state',
    response_data_field: 'start',
  },
  end_location_prefill: {
    city_field: 'end_city',
    state_field: 'end_state',
    response_data_field: 'end',
  },
}

//constants
const ANYWHERE = 'ANYWHERE'
const CURRENT_JOB = 'CURRENT_JOB'
const CURRENT_VEHICLE_LOCATION = 'CURRENT_VEHICLE_LOCATION'
const CUSTOM = 'CUSTOM'
const DWELL_0_5H = 'DWELL_0_5H'
const DWELL_1H = 'DWELL_1H'
const TERMINAL_LOCATION = 'TERMINAL_LOCATION'
const V1 = 'V1'

const SHOW_LOCATION_VALUES = [CURRENT_JOB, CUSTOM, TERMINAL_LOCATION]

const DATE_END_CHOICES = [
  { id: 7, name: 'Start + 7 days' },
  { id: 8, name: 'Start + 8 days' },
  { id: CUSTOM, name: 'Custom' },
]
const DWELL_TIME_CHOICES = [
  { id: V1, name: '2 hours' },
  { id: DWELL_1H, name: '1 hour' },
  { id: DWELL_0_5H, name: '30 minutes' },
]
const START_PREFILL_CHOICES = [
  { id: CURRENT_VEHICLE_LOCATION, name: 'Current driver vehicle location' },
  { id: CURRENT_JOB, name: 'Current job before start date' },
  { id: TERMINAL_LOCATION, name: 'Terminal location' },
  { id: CUSTOM, name: 'Custom' },
]
const END_PREFILL_CHOICES = [
  { id: ANYWHERE, name: 'Anywhere' },
  { id: CURRENT_VEHICLE_LOCATION, name: 'Current driver vehicle location' },
  { id: CURRENT_JOB, name: 'Current job after end date' },
  { id: TERMINAL_LOCATION, name: 'Terminal location' },
  { id: CUSTOM, name: 'Custom' },
]

//utils
const location_from_values = (prefill: string, city: string, state: string) => {
  switch (prefill) {
    case ANYWHERE:
      return ''
    case TERMINAL_LOCATION:
      return TERMINAL_LOCATION
    case CURRENT_VEHICLE_LOCATION:
      return CURRENT_VEHICLE_LOCATION
    default:
      return [city, state].join(', ')
  }
}

/**
 * Convert location to form location fields
 * @param {string} location
 * @return {array} [prefill, city, state]
 */
const values_from_location = (location: string) => {
  switch (location) {
    case '':
      return [ANYWHERE, '', '']
    case TERMINAL_LOCATION:
      return [TERMINAL_LOCATION, '', '']
    case CURRENT_VEHICLE_LOCATION:
      return [CURRENT_VEHICLE_LOCATION, '', '']
    default:
      return [CUSTOM].concat(split(location, ', ', 2))
  }
}

const blocklist_from_inputs = (items: any) => items.map((item: any) => item.source_id.trim())
const inputs_from_blocklist = (items: any) =>
  items.map((item: any) => {
    return { source_id: item }
  })

/**
 * Format form values from ScheduleGenerate for request to backend API
 * @param {object} values
 * @return {object}
 */
export function ScheduleFormToAPI(record: any, values: any) {
  values = sanitizeEmptyValues(record, values)

  const date_start_min = values['date_start_min'].toISO({ includeOffset: false })
  const date_end_max = values['date_end_max'].toISO({ includeOffset: false })

  const organized_values = {
    driver_preferences: {
      equipment: values['equipment'],
      miles_per_hour: values['miles_per_hour'],
      terminal_city: values['terminal_city'],
      terminal_state: values['terminal_state'],
      tractor_type: values['tractor_type'],
      truck_weight_lb: values['truck_weight_lb'],
    },
    schedule: {
      driver: values['user'],
      share_with_driver: values['share_with_driver'],
    },
    schedule_options: {
      away_from_home_hours_max: values['away_from_home_hours_max'],
      booking_type: values['instant_book_only'] ? 'INSTANT' : 'ALL',
      date_end_max: date_end_max,
      date_start_min: date_start_min,
      deadhead_mi_max: values['deadhead_mi_max'],
      load_providers: values['load_providers'],
      on_duty_hours_max: values['on_duty_hours_max'],
      initialize_from_driver_jobs: values['initialize_from_driver_jobs'],
      initialize_hos_from_eld: values['initialize_hos_from_eld'],
      end_location: location_from_values(
        values['end_location_prefill'],
        values['end_city'],
        values['end_state']
      ),
      start_location: location_from_values(
        values['start_location_prefill'],
        values['start_city'],
        values['start_state']
      ),
      optimizer_config_version: values['optimizer_config_version'],
      source_id_blacklist:
        values['source_id_blacklist'] && blocklist_from_inputs(values['source_id_blacklist']),
    },
  }

  if (values['include_states']) {
    set(organized_values, 'schedule_options.operating_states', values['state_id_filter'])
  } else {
    set(organized_values, 'schedule_options.state_id_blacklist', values['state_id_filter'])
  }

  return organized_values
}

/**
 * Format serialized schedule record to form values for Schedule Generate
 * Used to pre-populate form to regenerate (+edit) a schedule.
 * @param {object} record
 * @return {object}
 */
export function ScheduleRecordtoForm(record: any) {
  const metadata = get(record, 'metadata', {})
  const schedule_options = get(metadata, 'schedule_options', {})
  const driver_preferences = get(schedule_options, 'driver_preferences', {})

  const driver = get(driver_preferences, 'driver', null)
  const label = 'Regenerated Schedule.'.concat(get(record, 'label', ''))
  const group_id = get(record, 'group_id', null)
  const share_with_driver = get(record, 'share_with_driver', false)
  const [start_location_prefill, start_city, start_state] = values_from_location(
    get(schedule_options, 'start_location', '')
  )
  const [end_location_prefill, end_city, end_state] = values_from_location(
    get(schedule_options, 'end_location', '')
  )
  const operating_states = get(schedule_options, 'operating_states', [])
  const state_id_blacklist = get(schedule_options, 'state_id_blacklist', [])
  const state_id_filter = operating_states.length ? operating_states : state_id_blacklist
  const include_states = operating_states.length > 0
  const date_start_min = get(schedule_options, 'date_start_min', null)
  const date_end_max = get(schedule_options, 'date_end_max', null)
  const source_id_blacklist = inputs_from_blocklist(get(schedule_options, 'source_id_blacklist', []))

  const custom_values = {
    ...{
      share_with_driver,
      start_location_prefill,
      start_city,
      start_state,
      end_location_prefill,
      end_city,
      end_state,
      state_id_filter,
      include_states,
    },
    ...(driver && { user: driver }),
    ...(label && { label }),
    ...(group_id && { group_id }),
    ...(date_start_min && { date_start_min: DateTime.fromISO(date_start_min) }),
    ...(date_end_max && { date_end_max: DateTime.fromISO(date_end_max) }),
    ...{ date_end_select: CUSTOM },
    ...(source_id_blacklist.length && { source_id_blacklist }),
  }

  return {
    ...omit(driver_preferences, ['driver']),
    ...omit(schedule_options, ['driver_preferences']),
    ...custom_values,
  }
}

const GenerateScheduleForm = ({ record, version, initialValues }: any) => {
  const redirectTo = useRedirect()
  const notify = useNotify()
  const dataProvider = useDataProvider()

  // update some form fields when other fields change
  const calculator = createDecorator(
    {
      field: 'user', // when user changes...
      updates: (value) => {
        const userId = value
        if (userId) {
          return dataProvider.getOne('users', { id: userId }).then(({ data }) => {
            const driverPreferences = data['driver']['driver_preferences']
            if (driverPreferences) {
              return pick(driverPreferences, driverPreferencesFields)
            }
          })
        }

        return {}
      },
    },
    {
      field: /(date_start_min|date_end_select)/,
      updates: (value, name, allValues: any) => {
        if (allValues['date_end_select'] === CUSTOM) {
          return {}
        }

        if (allValues['date_start_min']) {
          // Parse end date according to form options.
          const date_end_max = allValues['date_start_min'].plus({ days: allValues['date_end_select'] })
          return { date_end_max: date_end_max }
        }

        return {}
      },
    },
    {
      field: /(start_location_prefill|end_location_prefill)/,
      updates: (value, name, allValues: any) => {
        const { city_field, state_field, response_data_field } = locationPrefillFields[name]

        if (value === TERMINAL_LOCATION) {
          return {
            [city_field]: allValues['terminal_city'],
            [state_field]: allValues['terminal_state'],
          }
        }

        if (value === CURRENT_JOB) {
          return prefillLocations(
            allValues['date_start_min'].toISO(),
            allValues['date_end_max'].toISO(),
            allValues['user']
          ).then((response) => {
            if (response) {
              const data = response['data'][response_data_field]
              if (data) {
                const stop = get(data, `job.load.stops[${data['stop_index']}]`)
                return {
                  [city_field]: stop['location_city'],
                  [state_field]: stop['location_state'],
                }
              }
            }
            return {}
          })
        }

        if (value === CUSTOM) {
          return {}
        }

        return {
          [city_field]: '',
          [state_field]: '',
        }
      },
    },
    {
      field: 'state_id_zones_filter',
      updates: (value) => {
        const selected_zone_ids = value
        const new_state_ids = ZIP_ZONE_STATES.filter((zip_zone) =>
          selected_zone_ids.includes(zip_zone['id'])
        ).map((zip_zone) => zip_zone['state_ids'])

        return {
          state_id_filter: union(...new_state_ids),
        }
      },
    }
  )

  const submit = (values: any) => {
    const organized_values = ScheduleFormToAPI(record, values)
    generateSchedule(organized_values, notify, redirectTo)
  }

  var initialValuesObject = merge(
    {
      miles_per_hour: '50',
      equipment: ['DRY_VAN'],
      tractor_type: 'SLEEPER',
      load_providers: PROVIDER_CHOICES.map((item) => item.id),
      date_end_select: 7,
      initialize_from_driver_jobs: false,
      share_with_driver: false,
      optimizer_config_version: V1,
      end_location_prefill: ANYWHERE,
      start_location_prefill: CUSTOM,
    },
    initialValues
  )

  const driverTextRenderer = (choice: any) =>
    choice && `${choice.first_name} ${choice.last_name} (${choice.email})`

  return (
    <Form
      initialValues={initialValuesObject}
      onSubmit={submit}
      mutators={{ ...arrayMutators }}
      decorators={[calculator]}
      key={version}
      render={(formProps) => {
        return (
          <form onSubmit={formProps.handleSubmit}>
            <Typography variant="h6" gutterBottom>
              Driver
            </Typography>
            <Typography variant="caption">
              Select a driver and if that driver has saved preferences (if a schedule was previously generated
              for them) then they will auto-populate. Note that selecting a driver without saved preferences
              does not clear populated values.
            </Typography>
            <Box display="flex">
              <Box flex={1}>
                <ReferenceInput
                  source="id"
                  reference="users"
                  name="user"
                  perPage={25}
                  filterToQuery={(searchText) => ({ search: searchText })}
                  label="Driver"
                  validate={validateRequired}
                >
                  <AutocompleteInput optionText={driverTextRenderer} optionValue="id" fullWidth={true} />
                </ReferenceInput>
              </Box>
              <Box flex={1}></Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <TextInput
                  source="terminal_city"
                  name="terminal_city"
                  label="Terminal city"
                  validate={validateRequired}
                />
              </Box>
              <Box flex={1}>
                <AutocompleteInput
                  name="terminal_state"
                  optionText="abbreviation"
                  optionValue="abbreviation"
                  label="Terminal state"
                  choices={US_STATES}
                  validate={validateRequired}
                />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <CheckboxGroupInput
                  name="equipment"
                  label="Equipment (Trailer) types"
                  choices={SUPPORTED_EQUIPMENT_CHOICES}
                  validate={validateRequired}
                />
              </Box>
              <Box flex={1}>
                <SelectInput
                  name="tractor_type"
                  label="Tractor type"
                  choices={TRACTOR_CHOICES}
                  validate={validateRequired}
                />
              </Box>
              <Box flex={1}>
                <NumberInput
                  source="miles_per_hour"
                  name="miles_per_hour"
                  label="Average miles per hour"
                  min="0"
                  max="100"
                  step="1"
                  validate={validateRequired}
                />
              </Box>
              <Box flex={1}>
                <NumberInput
                  source="truck_weight_lb"
                  name="truck_weight_lb"
                  label="Max load weight (lbs)"
                  min="0"
                  step="1000"
                />
              </Box>
            </Box>
            <Divider />
            <Typography variant="h6" gutterBottom>
              Date Range
            </Typography>
            <Typography variant="caption">
              When initializing HOS from driver's ELD (see Other section), a start date in the past will be
              automatically moved forward to now.
            </Typography>
            <Box display="flex">
              <Box flex={1}>
                <Labeled label="Earliest start date">
                  <DateTimeInput24hr name="date_start_min" />
                </Labeled>
              </Box>
              <Box flex={1}>
                <SelectInput name="date_end_select" label="Latest end date" choices={DATE_END_CHOICES} />
                <FormDataConsumer>
                  {({ formData, ...rest }) =>
                    formData.date_end_select === CUSTOM && (
                      <Labeled label="Custom end date">
                        <DateTimeInput24hr name="date_end_max" {...rest} />
                      </Labeled>
                    )
                  }
                </FormDataConsumer>
              </Box>
            </Box>
            <Divider />
            <Typography variant="h6" gutterBottom>
              Location
            </Typography>
            <Typography variant="caption">
              Selecting "current job" prefilled location will prefill the schedule's start (end) location with
              a stop on a user's existing job near the start (end) date. If no suitable stop is found, the
              start (end) city and state fields won't change. Selecting "current location" uses the driver
              vehicle's current location via its ELD device. If the driver does not have an active Samsara ELD
              device, you'll see an ERROR after clicking SUBMIT. Selecting "custom" clears the prefilled
              location.
            </Typography>

            <Box display="flex">
              <Box flex={2}>
                <SelectInput
                  name="start_location_prefill"
                  label="Prefill Start Location"
                  choices={START_PREFILL_CHOICES}
                  validate={validateRequired}
                />
              </Box>
              <ShowIfContains field_name="start_location_prefill" contain_values={SHOW_LOCATION_VALUES}>
                <Box flex={1}>
                  <TextInput source="start_city" name="start_city" label="Start city" />
                </Box>
                <Box flex={1}>
                  <AutocompleteInput
                    name="start_state"
                    optionText="abbreviation"
                    optionValue="abbreviation"
                    label="Start state"
                    choices={US_STATES}
                  />
                </Box>
              </ShowIfContains>
            </Box>
            <Box display="flex">
              <Box flex={2}>
                <SelectInput
                  name="end_location_prefill"
                  label="Prefill End Location"
                  choices={END_PREFILL_CHOICES}
                  validate={validateRequired}
                />
              </Box>
              <ShowIfContains field_name="end_location_prefill" contain_values={SHOW_LOCATION_VALUES}>
                <Box flex={1}>
                  <TextInput source="end_city" name="end_city" label="End city" />
                </Box>
                <Box flex={1}>
                  <AutocompleteInput
                    name="end_state"
                    optionText="abbreviation"
                    optionValue="abbreviation"
                    label="End state"
                    choices={US_STATES}
                  />
                </Box>
              </ShowIfContains>
            </Box>
            <Divider />
            <Typography variant="h6" gutterBottom>
              Other
            </Typography>
            <Box display="flex">
              <Box flex={1}>
                <NumberInput
                  source="on_duty_hours_max"
                  name="on_duty_hours_max"
                  label="On duty hours"
                  min="0"
                  max="1000"
                  step="1"
                  helperText="Max drive + dwell hours"
                />
              </Box>
              <Box flex={1}>
                <NumberInput
                  source="away_from_home_hours_max"
                  name="away_from_home_hours_max"
                  label="Away from home hours"
                  helperText="Max hours before returning to terminal"
                  min="0"
                  max="1000"
                  step="1"
                />
              </Box>
              <Box flex={1}>
                <SelectInput
                  name="optimizer_config_version"
                  label="Dwell time"
                  helperText="Time spent (on average) at each stop"
                  choices={DWELL_TIME_CHOICES}
                  validate={validateRequired}
                />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <NumberInput
                  source="deadhead_mi_max"
                  name="deadhead_mi_max"
                  label="Max deadhead miles"
                  min="0"
                  max="1000"
                  step="1"
                  helperText="Max deadhead for any stop on a schedule"
                />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <CheckboxGroupInput
                  name="load_providers"
                  label="Load Providers"
                  choices={PROVIDER_CHOICES}
                  validate={validateRequired}
                />
              </Box>
              <Box flex={1}>
                <BooleanInput
                  source="instant_book_only"
                  name="instant_book_only"
                  label="Instant book only loads"
                />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <BooleanInput
                  source="include_states"
                  name="include_states"
                  label="Include States"
                  helperText="State filter includes states, otherwise excludes."
                />
              </Box>
              <Box flex={1}>
                <FormDataConsumer>
                  {({ formData }) => (
                    <AutocompleteArrayInput
                      name="state_id_zones_filter"
                      optionText="name"
                      optionValue="id"
                      label={`${formData.include_states ? 'Included' : 'Excluded'} DAT Zip Zones`}
                      helperText="Adding/removing Zones resets States list."
                      choices={ZIP_ZONE_STATES}
                    />
                  )}
                </FormDataConsumer>
              </Box>
              <Box flex={1}>
                <FormDataConsumer>
                  {({ formData }) => (
                    <AutocompleteArrayInput
                      name="state_id_filter"
                      optionText="abbreviation"
                      optionValue="abbreviation"
                      label={`${formData.include_states ? 'Included' : 'Excluded'} States`}
                      choices={US_STATES}
                    />
                  )}
                </FormDataConsumer>
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <ArrayInput source="source_id_blacklist" label="Exclude Source IDs (not human ids)">
                  <SimpleFormIterator>
                    <TextInput source="source_id" multiline label="Source Id" />
                  </SimpleFormIterator>
                </ArrayInput>
              </Box>
              <Box flex={2}></Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <BooleanInput
                  source="initialize_from_driver_jobs"
                  name="initialize_from_driver_jobs"
                  label="Initialize with driver's existing jobs."
                />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <BooleanInput
                  source="initialize_hos_from_eld"
                  name="initialize_hos_from_eld"
                  label="Initialize HOS clocks from driver's ELD."
                  helperText="WARNING: An error is returned if driver does not have an active Samsara ELD."
                />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <BooleanInput
                  source="share_with_driver"
                  name="share_with_driver"
                  label="Share schedule with driver"
                  helperText="WARNING: Toggling will surface schedule in driver's App."
                />
              </Box>
            </Box>

            <Toolbar>
              <Box display="flex" justifyContent="space-between" width="100%">
                <Button variant="contained" color="primary" type="submit">
                  Submit
                </Button>
              </Box>
            </Toolbar>
          </form>
        )
      }}
    />
  )
}

export default function ScheduleGenerate(props: any) {
  const initialValues = get(props, `location.state.initial_values`, {})
  return <GenerateScheduleForm initialValues={initialValues} />
}
